/**
 * Icon components for the search widget
 */
import React, { useEffect, useState } from "react";
import { getHostEl } from "../utils/hostElements";
import { normalizeFAFamily } from "../utils/helpers";

/**
 * Read Filter icon spec from host data-attrs
 */
export function readFilterIconSpec() {
  const host = getHostEl();
  if (!host) {
    return { mode: "fa", faFamily: "fas", faIcon: "fa-sliders", size: 16, color: "#0f172a", svg: "", show: true };
  }
  const ds = host.dataset;
  return {
    mode: (ds.filterIconMode || "fa").toLowerCase(),
    faFamily: normalizeFAFamily(ds.filterIconFamily || "fa-solid"),
    faIcon: ds.filterIconName || "fa-sliders",
    size: Number(ds.filterIconSize || 16),
    color: ds.filterIconColor || "#0f172a",
    svg: ds.filterIconSvg || "",
    show: ds.filterIconShow !== "false"
  };
}

/**
 * Filter icon component with live data attribute updates
 */
export function FilterIcon() {
  const [spec, setSpec] = useState(readFilterIconSpec);

  useEffect(() => {
    const host = getHostEl();
    if (!host) return;
    const sync = () => setSpec(readFilterIconSpec());
    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type !== "attributes") continue;
        const n = m.attributeName || "";
        if (
          n === "data-filter-icon-mode" ||
          n === "data-filter-icon-family" ||
          n === "data-filter-icon-name" ||
          n === "data-filter-icon-size" ||
          n === "data-filter-icon-color" ||
          n === "data-filter-icon-svg" ||
          n === "data-filter-icon-show"
        ) {
          sync();
        }
      }
    });
    mo.observe(host, { attributes: true });
    return () => mo.disconnect();
  }, []);

  if (!spec.show) return null;

  if (spec.mode === "svg" && spec.svg) {
    return (
      <span
        aria-hidden="true"
        style={{ width: spec.size, height: spec.size, display: "inline-grid", placeItems: "center" }}
        dangerouslySetInnerHTML={{ __html: spec.svg }}
      />
    );
  }
  return <i aria-hidden="true" className={`${spec.faFamily} ${spec.faIcon}`} style={{ fontSize: spec.size, color: spec.color }} />;
}

/**
 * Read Search button icon spec from host data-attrs
 */
export function readSearchButtonIconSpec() {
  const host = getHostEl();
  if (!host) {
    return { mode: "fa", faFamily: "fas", faIcon: "fa-search", size: 16, color: "#ffffff", svg: "", show: true };
  }
  const ds = host.dataset;
  return {
    mode: (ds.searchbtnIconMode || "fa").toLowerCase(),
    faFamily: normalizeFAFamily(ds.searchbtnIconFamily || "fa-solid"),
    faIcon: ds.searchbtnIconName || "fa-search",
    size: Number(ds.searchbtnIconSize || 16),
    color: ds.searchbtnIconColor || "#ffffff",
    svg: ds.searchbtnIconSvg || "",
    show: ds.searchbtnIconShow !== "false"
  };
}

/**
 * Search button icon component with live data attribute updates
 */
export function SearchButtonIcon() {
  const [spec, setSpec] = useState(readSearchButtonIconSpec);

  useEffect(() => {
    const host = getHostEl();
    if (!host) return;
    const sync = () => setSpec(readSearchButtonIconSpec());
    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type !== "attributes") continue;
        const n = m.attributeName || "";
        if (
          n === "data-searchbtn-icon-mode" ||
          n === "data-searchbtn-icon-family" ||
          n === "data-searchbtn-icon-name" ||
          n === "data-searchbtn-icon-size" ||
          n === "data-searchbtn-icon-color" ||
          n === "data-searchbtn-icon-svg" ||
          n === "data-searchbtn-icon-show"
        ) {
          sync();
        }
      }
    });
    mo.observe(host, { attributes: true });
    return () => mo.disconnect();
  }, []);

  if (!spec.show) return null;

  if (spec.mode === "svg" && spec.svg) {
    return (
      <span
        aria-hidden="true"
        style={{ width: spec.size, height: spec.size, display: "inline-grid", placeItems: "center" }}
        dangerouslySetInnerHTML={{ __html: spec.svg }}
      />
    );
  }
  return <i aria-hidden="true" className={`${spec.faFamily} ${spec.faIcon}`} style={{ fontSize: spec.size, color: spec.color }} />;
}
